import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectInstance } from 'react-select';
import AsyncSelect from 'react-select/async';

import "react-datepicker/dist/react-datepicker.css";
import { CreditCardClassifyFormsI } from '../../../resources/form-props';
import { CreditCardClassificationValidate } from '../../../resources/form-validator';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';
import { useCreditCardApi } from '../../../resources/hooks/api/creditCardApiHook';
import { useGlClassificationApi } from '../../../resources/hooks/api/glClassificationApi';
import { useTransTableDataApi } from '../../../resources/hooks/api/transApiHook';
import { getAccountSettings, getThirdPartyImportedEndDate } from '../../../store/settings/selectors';
import { getBranch } from '../../../store/user/selectors';

// import '../GlClassification/GlClassification.css'
import Pagination from '@mui/material/Pagination';
import SAlert from 'components/SAlert';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SelectCustomOption from '../../../components/SelectCustomOption';
import { GL_VP, SELECT_CUSTOM_STYLE, URLS } from '../../../resources/constants';
import { calculateMaxHeightContent, usdCurrencyFormat } from '../../../resources/functions';
import '../GlClassification.css';
interface Props {

}

const CreditCardGlClassification: React.FC<Props> = () => {

    const { glReconcileTransactionSubmit, glReconcileTransactionlist } = useGlClassificationApi();
    const { fetchCreditCardCsvDetails } = useCreditCardApi();
    const { createTransactionAdjustment, discardTransaction } = useTransTableDataApi()
    const { glAccountsList } = useChartAccountApi();
    const selectRefs = useRef<SelectInstance<any, false, any>[]>([]);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [confirmMessage, setConfirmMessage] = useState<string>('');
    const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);
    const [discardConfirmMessage, setDiscardConfirmMessage] = useState<string>('');
    const [selectedData, setSelectedData] = useState<any>(null);
    const { t } = useTranslation("scenes.glclassification.credit_card");

    const routeParams = useParams();
    const navigate = useNavigate();
    const branchData = useSelector(getBranch)
    const accountSettings = useSelector(getAccountSettings)
    const thirdPartyImportedEndDate = useSelector(getThirdPartyImportedEndDate)

    const [records, setRecords] = useState<any>({
        start_date: new Date(),
        end_date: new Date(),
        account_name: '',
        account: '',
        account_id: 0,
        file_name: '',
        classify_status: false,
        total_records: 1000,
        records: []
    });
    const [reconciliationData, setReconciliationData] = useState<any>(null);
    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd'));
    const [searchParams, setSearchParams] = useState<any>({
        id: null,
        page: 1,
        per_page: 100
    });
    const [dropdownOptions, setDropdownOptions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [duplicateExists, setDuplicateExists] = useState<boolean>(false);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);
    const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
    const [BBCreatedDate, setBBCreatedDate] = useState<any | null>(null);
    const checkAllRef = useRef<HTMLInputElement>(null);
    const [sortConfig, setSortConfig] = useState<{ key: 'transaction_at' | 'description' | null; direction: 'asc' | 'desc' }>({
        key: null,
        direction: 'asc'
    });
    const { register, control, trigger, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm<CreditCardClassifyFormsI>({
        defaultValues: {
            classifications: []
        },
        resolver: yupResolver(CreditCardClassificationValidate)
    });
    const { fields, append: formAppend, remove: formRemove } = useFieldArray({
        control,
        name: "classifications"
    });

    useEffect(() => {
        if (routeParams['id'] && !isNaN(+routeParams['id'])) {
            setSearchParams((prevState: any) => ({
                ...prevState,
                id: routeParams['id']
            }))
        }
    }, [routeParams])

    useEffect(() => {
        if (accountSettings && (accountSettings.opening_balance_created_at || accountSettings.opening_balance_skipped_at)) {
            setBBCreatedDate(moment(accountSettings.opening_balance_created_at || accountSettings.opening_balance_skipped_at).endOf('month'));
        } else {
            setBBCreatedDate(null);
        }
    }, [accountSettings])

    useEffect(() => {
        if (searchParams['id'] && branchData?.id) {
            fetchTransactionList(+searchParams['id']);
            filterDebitAccountList('');
        }
    }, [searchParams, branchData?.id])

    useEffect(() => {
        const duplicate = records['records'].some((record: any) => record['duplicate']);
        setDuplicateExists(duplicate);

        records['records'].forEach((record: any, index: number) => {
            formAppend({
                formIndex: index,
                // checked: !record['posted'],
                checked: !(record['duplicate'] || record['posted']),
                id: record['id'],
                description: record['description'],
                transaction_at: record['transaction_at'],
                adjustment_debit: record['amount'],
                adjustment_credit: record['amount'],
                adjustment_master_debit_account_id: (record['debit_adjustment_account_id'] && {
                    label: record['name'] + `(${record['account']})`,
                    value: record['debit_adjustment_account_id']
                }) || null,
                adjustment_master_credit_account_id: null,
                duplicate: !!record['duplicate'],
                posted: !!record['posted'],
                allow_to_post: (BBCreatedDate ? !moment(record['transaction_at']).isSameOrBefore(BBCreatedDate, 'month') : true) && (thirdPartyImportedEndDate ? !moment(record['transaction_at']).isSameOrBefore(thirdPartyImportedEndDate, 'month') : true)
            })
        })
    }, [records['records']])

    useEffect(() => {
        if (reconciliationData) {
            const data: any = {};

            data['beginningBalance'] = reconciliationData.opening_balance_amount
            data['endingBalance'] = reconciliationData.closing_balance_amount
            data['cleared_balance_amount'] = reconciliationData.cleared_balance_amount
            data['difference_amount'] = reconciliationData.difference_amount

            // setFormParams((prevState: any) => ({
            //     ...prevState,
            //     ...data
            // }))
        }

    }, [reconciliationData])

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    })
    useEffect(() => {
        _handleOptionsFetching();
    }, [])

    useEffect(() => {
        // Always uncheck header checkbox on first load
        if (checkAllRef.current) {
            // Only check if all rows are checked and there are no duplicates
            const allChecked = (fields.length > 0) && fields.every(f => f.checked /*&& !f.duplicate*/);
            checkAllRef.current.checked = allChecked;
        }
    }, [fields])

    const fetchTransactionList = (id: number) => {
        if (!branchData?.id) {
            setIsLoading(false);
            return;
        }
        const params: any = {
            id,
            branch_id: +branchData['id'],
            "page": searchParams['page'],
            "per_page": searchParams['per_page'],
        }

        setIsLoading(true);
        fetchCreditCardCsvDetails(params, (message: string, resp: any) => {
            setIsLoading(false);
            try {
                if (resp?.data?.data) {
                    setRecords({
                        start_date: resp.data.data.start_date,
                        end_date: resp.data.data.end_date,
                        account_name: resp.data.data.account_name,
                        account: resp.data.data.account,
                        account_id: resp.data.data.account_id,
                        file_name: resp.data.data.file_name,
                        classify_status: !!resp.data.data.classify_status,
                        total_records: resp.data.data.total,
                        records: resp.data.data.items || []
                    });
                }
            } catch (err) {
                console.error('Error setting credit card records:', err);
            }
        }, (message: string, resp: any) => {
            setIsLoading(false);
            if (message && message !== 'Request cancelled') {
                toast.error(message);
            }
        })
    }

    const fechDebitAccountList = (searchText: string = ''): Promise<any[]> => {
        return new Promise<any>((resolve, reject) => {
            if (!branchData?.id) {
                return resolve([]);
            }
            const params: any = {
                "page": 1,
                "per_page": 10,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }
            // params['parent_account_in'] = GL_VP.DEBIT_CARD.exclude_accounts.join(',');
            glAccountsList(params, (message: string, resp: any) => {
                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id']
                }));
                return resolve(data);
                //console.log(data);
                // setGlAccount(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        })
    }

    const _handleOptionsFetching = async () => {
        const data = await fechDebitAccountList('');
        setDropdownOptions(data)
    }

    const filterDebitAccountList = async (inputValue: string) => {
        const data = await fechDebitAccountList(inputValue)
        return data;
    };


    const handleCheckAllChange = (e: any) => {
        if (checkAllRef.current) {
            checkAllRef.current.checked = e.target.checked;

            records['records'].forEach((record: any, index: number) => {
                // if (!(record['duplicate'] || record['posted'])) {
                if (!record['posted']) {
                    setValue(`classifications.${index}.checked`, e.target.checked)
                }
            })
        }
    };

    const handleCheckAll = (e: any, index: number) => {
        setValue(`classifications.${index}.checked`, e.target.checked);
        if (e.target.checked) {
            const checkedRecords = records['records'].filter((record: any, index: number) => getValues(`classifications.${index}.checked`) === true);

            if (checkedRecords.length == records['records'].length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = true;
                }
            }
        } else {
            const uncheckedRecords = records['records'].filter((record: any, index: number) => getValues(`classifications.${index}.checked`) === false);

            if (uncheckedRecords.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = false;
                }
            }
        }
    }

    const handleConfirm = (data: any) => {
        const duplicateItems = data['classifications'].filter((d: any) => d['duplicate'] && d['checked']);
        if (duplicateItems.length > 0) {
            setConfirmMessage('There ' + (duplicateItems.length > 1 ? 'are ' : 'is ') + duplicateItems.length + ' duplicate record' + (duplicateItems.length > 1 ? 's' : '') + ' selected.' + t('modal.confirm_overwrite'));
            setShowConfirm(true);
            setSelectedData(data);
        } else {
            processData(data);
        }
    }

    const processData = (data: any, action?: "overwrite" | "keep_both") => {
        const toRemoveItems: number[] = [];
        let txnBBEDateExists = false;
        const selectedRecords = data['classifications']
            .map((d: any, index: number) => {
                if (d.checked && !d.allow_to_post) {
                    txnBBEDateExists = true;
                    return null;
                }
                else if (d['checked']) {
                    toRemoveItems.push(index);
                    return {
                        id: d.id,
                        description: d.description,
                        transaction_at: d.transaction_at,
                        adjustment_debit: d.adjustment_debit,
                        adjustment_credit: d.adjustment_credit,
                        adjustment_master_debit_account_id: (d.adjustment_master_debit_account_id && +d.adjustment_master_debit_account_id.value) || 0,
                        adjustment_master_credit_account_id: +records['account_id'],
                        overwrite: (action === "overwrite") && d.duplicate ? true : false,
                        keep: (action === "keep_both") && d.duplicate ? true : false
                    };
                } else {
                    return null;
                }
            })
            .filter((e: any) => e); // Filter out null values
        if (txnBBEDateExists) {
            toast.error(t('alert.beginning_balance_info'));
            return;
        }
        else if (selectedRecords.length > 0) {
            postData(selectedRecords, toRemoveItems);
        } else {
            toast.error(t('alert.select_record'));
            return;
        }
        setSelectedData(null)
    };

    const postData = (records: any, toRemoveItems: number[]) => {
        console.log('Posting data:', records);
        setIsLoading(true);

        createTransactionAdjustment({ params: records, branch_id: +branchData['id'] }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.success(message)

            reset({
                classifications: []
            })

            setSearchParams((prevState: any) => ({
                ...prevState,
                page: 1
            }));

            setShowConfirm(false)

            // formRemove(toRemoveItems)
            // if (records.length == fields.length) {
            //     navigate(-1);
            // }
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const handleDiscardConfirm = (data: any) => {
        const items = data['classifications'].filter((d: any) => d['checked']);
        if (items.length > 0) {
            setSelectedData(data);
            setDiscardConfirmMessage(t('modal.confirm_discard'));
            setShowDiscardConfirm(true);
        } else {
            toast.error(t('alert.select_record_discard'));
        }
    };

    const discardTransactions = (data: any) => {
        const transaction_ids: number[] = data['classifications']
            .map((d: any) => d['checked'] ? d.id : null)
            .filter((id: any) => id !== null);

        setIsLoading(true);

        discardTransaction({ params: { transaction_ids }, branch_id: +branchData['id'] }, (message: string, resp: any) => {

            setIsLoading(false);
            toast.success(message)

            reset({ classifications: [] })
            setSearchParams((prevState: any) => ({ ...prevState, page: 1 }));

            setShowDiscardConfirm(false);

        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        });
    };

    const handleSelectChange = (displayIndex: number, e: any) => {
        setTimeout(() => {
            let nextDisplayIndex = displayIndex + 1;
            while (nextDisplayIndex < sortedIndices.length) {
                const targetFieldIndex = sortedIndices[nextDisplayIndex];
                const nextSelect = selectRefs.current[targetFieldIndex];
                if (nextSelect && !nextSelect.props.isDisabled) {
                    nextSelect.focus();
                    break;
                }
                nextDisplayIndex++;
            }
        }, 0);
    };

    const onChangePage = (event: any, page: number) => {
        reset({
            classifications: []
        })
        setSearchParams((prevState: any) => ({
            ...prevState,
            page: page
        }))
    }

    const handleDropdownFocus = (index: number) => {
        setFocusedRowIndex(index); // Update the focused row index
    };

    const handleAutoSelectCreditCardDropdown = (displayIndex: number) => {
        const currentFieldIndex = sortedIndices[displayIndex];
        const selectedValue = getValues(`classifications.${currentFieldIndex}.adjustment_master_debit_account_id`);
        for (let i = displayIndex + 1; i < sortedIndices.length; i++) {
            const nextFieldIndex = sortedIndices[i];
            if (!fields[nextFieldIndex]?.posted) {
                setValue(`classifications.${nextFieldIndex}.adjustment_master_debit_account_id`, selectedValue);
                selectRefs.current[nextFieldIndex]?.setValue(selectedValue, 'select-option');
            }
        }
    }

    const sortedIndices = useMemo(() => {
        const indices = fields.map((_, i) => i);

        if (!sortConfig.key) return indices;

        return indices.sort((a, b) => {
            let valA: any;
            let valB: any;

            if (sortConfig.key === 'transaction_at') {
                valA = moment(fields[a]?.transaction_at).valueOf();
                valB = moment(fields[b]?.transaction_at).valueOf();
            } else {
                valA = (fields[a]?.description || '').toLowerCase();
                valB = (fields[b]?.description || '').toLowerCase();
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [fields, sortConfig]);

    const handleSort = (key: 'transaction_at' | 'description') => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const getSortIcon = (key: 'transaction_at' | 'description') => {
        const isActive = sortConfig.key === key;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            className="ms-1"
            style={{ verticalAlign: 'middle' }}
        >
            <path
                d="M8 3L4 8h3v9h2V8h3L8 3z"
                fill={isActive && (sortConfig.direction === 'asc') ? '#0d6efd' : '#FFFFFF'}
            />
            <path
                d="M16 21l4-5h-3V7h-2v9h-3l4 5z"
                fill={isActive && (sortConfig.direction === 'desc') ? '#0d6efd' : '#FFFFFF'}
            />
        </svg>
    );
};
    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-8 align-items-center d-flex">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm me-3 d-inline-flex align-items-center gap-1 shadow-sm"
                                onClick={() => navigate(`${URLS.GL_CLASSIFICATION}?tab=creditCard`)}
                                title="Back to Credit Cards"
                            >
                                <i className="fa fa-arrow-left"></i>
                                <span>{t('button.back', 'Back')}</span>
                            </button>
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex">
                                {t('title') + moment(records['start_date']).format('Do MMM YYYY') + t('date.to') + moment(records['end_date']).format('Do MMM YYYY') + ' - ' + records['account_name'] + ' (' + records['account'] + ')'}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className=" executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Form>
                            {
                                (((fields.length > 0) && !records['classify_status']) && (
                                    <div className="modal-footer gap-2">
                                        <button type="button" className="btn btn-danger" onClick={handleSubmit(handleDiscardConfirm)}>{t('button.discard')}</button>
                                        <button type="button" className="btn btn-primary" onClick={handleSubmit(handleConfirm)}>{t('button.post')}</button>
                                    </div>
                                )) || <></>
                            }

                            <br></br>

                            {
                                duplicateExists ? (
                                    <Alert variant={'info'}>
                                        <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                                        {t('alert.duplicate_info')}
                                    </Alert>
                                ) : <></>
                            }


                            {/* <div className="table-responsive list-table table-scroll" style={{ maxHeight: contentMaxHeight }}> */}
                            <div className="table-responsive list-table table-scroll creditcard-table">
                                <table className="table table-default">
                                    <thead>
                                        <tr className="align-middle">
                                            <th className="w-5 text-center">
                                                <div className="form-check ">
                                                    <label >
                                                        {/* <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" disabled={fields.length > 0 && fields[0].id ? true : false} onChange={handleCheckAllChange} /> */}
                                                        <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" onChange={handleCheckAllChange} disabled={records['classify_status']} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th className="text-center w-5" style={{ cursor: 'pointer' }} onClick={() => handleSort('transaction_at')}>
                                                {t('table.date')}{getSortIcon('transaction_at')}
                                            </th>
                                            <th className="text-start" style={{ cursor: 'pointer' }} onClick={() => handleSort('description')}>
                                                {t('table.description')}{getSortIcon('description')}
                                            </th>
                                            <th className="text-start">{t('table.debit_gl_account')}</th>
                                            <th className="text-start w-5">{t('table.posted')}</th>
                                            <th className="text-end w-5">{t('table.amount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            fields.length > 0 ?
                                                sortedIndices.map((index: number, displayIndex: number) => {
                                                    const data = fields[index];
                                                    return (
                                                        <tr className={`align-middle ${focusedRowIndex === index ? 'focused-row' : ''
                                                            }`} key={data['id']}>
                                                            <td className={`text-center ${focusedRowIndex === index ? 'focused-row' : ''
                                                                }`}>
                                                                <div className="form-check ">
                                                                    <label >
                                                                        <input className="form-check-input wh-20 checkbox" {...register(`classifications.${index}.checked`)}
                                                                            name={`revenues.${index}.checked`}
                                                                            defaultChecked={fields[index]['checked']}
                                                                            disabled={fields[index].posted}
                                                                            // disabled={fields[index].duplicate || fields[index].posted}
                                                                            type="checkbox" onChange={(e: any) => handleCheckAll(e, index)} />
                                                                    </label>
                                                                </div>
                                                            </td>
                                                            <td className={`${fields[index].duplicate ? "text-danger text-start" : "text-start"} ${focusedRowIndex === index ? 'focused-row' : ''}`}>
                                                                <strong>{moment.utc(fields[index].transaction_at).format('Do MMM YYYY')}</strong>
                                                            </td>
                                                            <td className={`${fields[index].duplicate ? "text-danger text-start text-truncate" : "text-start text-truncate"} ${focusedRowIndex === index ? 'focused-row' : ''}`} style={{ maxWidth: '250px' }} title={fields[index].description || t('message.na')}>{fields[index].description || t('message.na')}</td>
                                                            <td className={`text-start ${focusedRowIndex === index ? 'focused-row' : ''}`}>
                                                                <Form.Group className="col-sm-12 d-flex" controlId="formBasicEmail">
                                                                    <Controller
                                                                        control={control}
                                                                        name={`classifications.${index}.adjustment_master_debit_account_id`}
                                                                        render={({ field: { value, onChange, onBlur } }) => (
                                                                            <AsyncSelect
                                                                                ref={(el) => (selectRefs.current[index] = el as SelectInstance<any, false, any>)}
                                                                                isClearable={true}
                                                                                placeholder={t('dropdown.placeholder.gl_account')}
                                                                                defaultOptions={dropdownOptions}
                                                                                loadOptions={filterDebitAccountList}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e);
                                                                                    if (e) {
                                                                                        handleSelectChange(displayIndex, e);
                                                                                    }
                                                                                }}
                                                                                onKeyDown={(e: React.KeyboardEvent) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        const value = getValues(`classifications.${index}.adjustment_master_debit_account_id`);
                                                                                        // If Enter is pressed, move focus to the next dropdown
                                                                                        if (value) {
                                                                                            handleSelectChange(displayIndex, getValues(`classifications.${index}.adjustment_master_debit_account_id`));
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                className='vp-dropdown'
                                                                                menuPosition='fixed'
                                                                                value={value}
                                                                                defaultValue={value}
                                                                                onFocus={() => handleDropdownFocus(index)} // Update focus on dropdown focus
                                                                                styles={selectCustomStyle}
                                                                                components={{ Option: SelectCustomOption }}
                                                                                // isDisabled={fields[index].posted || fields[index].duplicate}
                                                                                isDisabled={fields[index].posted}
                                                                            />
                                                                        )}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 ms-2"
                                                                        onClick={() => handleAutoSelectCreditCardDropdown(displayIndex)}
                                                                    >
                                                                        <i className="cursor-pointer fa fa-arrow-alt-circle-down" title={t('tooltip.apply_below')}></i>
                                                                    </button>
                                                                    {
                                                                        !!fields[index].adjustment_master_debit_account_id && <input {...register(`classifications.${index}.adjustment_master_debit_account_id`)} type="hidden" />
                                                                    }
                                                                </Form.Group>
                                                                {
                                                                    errors && errors.classifications && errors.classifications[index] && errors.classifications[index]?.adjustment_master_debit_account_id && errors.classifications[index]?.adjustment_master_debit_account_id?.message && (
                                                                        <Form.Text className="text-danger d-flex">
                                                                            <strong>
                                                                                {errors.classifications[index]?.adjustment_master_debit_account_id?.message as ReactNode || ''}
                                                                            </strong>
                                                                        </Form.Text>
                                                                    ) || ''
                                                                }
                                                            </td>
                                                            <td className={`${fields[index].duplicate ? "text-danger text-start" : "text-start"} ${focusedRowIndex === index ? 'focused-row' : ''}`}>{fields[index].posted ? 'Yes' : 'No'}</td>
                                                            <td className={`${fields[index].duplicate ? "text-danger text-end" : "text-end"} ${focusedRowIndex === index ? 'focused-row' : ''}`}>{usdCurrencyFormat(fields[index].adjustment_credit)}</td>
                                                        </tr>
                                                    )
                                                }) :
                                                <tr>
                                                    <td colSpan={5} className='text-center'>{t('empty.no_records')}</td>
                                                </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </Form>
                    </div>
                    <div className='d-flex justify-content-center'>
                        <Pagination count={Math.ceil(records['total_records'] / searchParams['per_page'])} showFirstButton showLastButton onChange={onChangePage} />
                    </div>

                </div>

                {showConfirm && confirmMessage && selectedData && (
                    <SAlert
                        show={showConfirm}
                        icon={'warning'}
                        showCancel={true}
                        showDenyButton={true}
                        confirmBtnText={t('button.overwrite')}
                        cancelBtnText={t('button.cancel')}
                        denyButtonText={t('button.keep_both')}
                        title={t('modal.confirm_delete.title')}
                        msg={confirmMessage}
                        onConfirm={() => processData(selectedData, "overwrite")}
                        onCancel={() => setShowConfirm(false)}
                        onDeny={() => processData(selectedData, "keep_both")}
                    />
                )}

                {showDiscardConfirm && discardConfirmMessage && selectedData && (
                    <SAlert
                        show={showDiscardConfirm}
                        icon={'warning'}
                        showCancel={true}
                        confirmBtnText={t('button.delete')}
                        cancelBtnText={t('button.cancel')}
                        title={t('modal.confirm_delete.title')}
                        msg={discardConfirmMessage}
                        onConfirm={() => discardTransactions(selectedData)}
                        onCancel={() => setShowDiscardConfirm(false)}
                    />
                )}
            </div>
        </div>

    )
}

export default CreditCardGlClassification;